/**
 * Soʻz ilon (demo `startSnake`): 4 ta soʻz harflar panjarasiga ilondek yashirilgan — barmoq bilan qoʻshni
 * kataklarni bogʻlab soʻz yasaladi. Panjara va yoʻllar bir marta quriladi; sudrash — pointer hodisalari + `elementFromPoint`.
 */
import { useEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { LANG, W, fmtTime, fold, shuffle } from '../../data';
import type { Word } from '../../data/types';
import { S } from '../../store';
import { nav } from '../../lib/nav';
import { chime } from '../../lib/sound';
import { speak } from '../../lib/speech';
import { haptic } from '../../lib/telegram';
import { toast } from '../../lib/toast';
import { applyCourse, fire, sync } from '../../lib/sync';
import { XPR, estXp } from '../../lib/xp';
import { t, tn } from '../../i18n';
import { Ic } from '../../ui/Icon';

const ALPHA = LANG === 'ru' ? 'АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЫЬЭЮЯ' : 'ABCDEFGHIJKLMNOPRSTUVWY';
const UP = (s: string) => s.toLocaleUpperCase(LANG === 'ru' ? 'ru' : 'en');
const COLS = ['#1A66F5', '#F97316', '#2e7a55', '#3f78a3'];

interface Puzzle { words: Word[]; N: number; g: string[]; paths: number[][] }

/** 3–6 harfli, faqat harflardan iborat, takrorlanmaydigan 4 ta soʻz (pool yetmasa — butun bazadan). */
function snakeWords(ws: Word[]): Word[] {
  const ok = (w: Word) => /^\p{L}+$/u.test(w.w) && w.w.length >= 3 && w.w.length <= 6;
  let c = shuffle(ws.filter(ok)); if (c.length < 3) c = shuffle(W.filter(ok));
  const out: Word[] = [], seen = new Set<string>();
  for (const w of c) { const k = fold(w.w); if (seen.has(k)) continue; seen.add(k); out.push(w); if (out.length === 4) break; }
  return out;
}

/** Soʻzlarni N×N panjaraga ilon yoʻllari (DFS) bilan joylash; qolgan kataklar — tasodifiy harflar. */
function placeSnake(words: Word[], N: number): Pick<Puzzle, 'g' | 'paths'> | null {
  const nb = (i: number) => { const r = Math.floor(i / N), c = i % N, o: number[] = []; if (r > 0) o.push(i - N); if (r < N - 1) o.push(i + N); if (c > 0) o.push(i - 1); if (c < N - 1) o.push(i + 1); return shuffle(o); };
  for (let attempt = 0; attempt < 300; attempt++) {
    const g: Array<string | null> = Array(N * N).fill(null), paths: number[][] = []; let fail = false;
    for (const w of words) {
      const L = [...UP(w.w)]; let path: number[] | null = null;
      const dfs = (p: number[]): number[] | null => {
        if (p.length === L.length) return p;
        for (const n of nb(p[p.length - 1]!)) if (g[n] === null && !p.includes(n)) { const r = dfs(p.concat(n)); if (r) return r; }
        return null;
      };
      for (const s of shuffle([...g.keys()].filter((i) => g[i] === null))) { path = dfs([s]); if (path) break; }
      if (!path) { fail = true; break; }
      path.forEach((i, k) => { g[i] = L[k]!; }); paths.push(path);
    }
    if (!fail) return { g: g.map((x) => x ?? ALPHA[Math.floor(Math.random() * ALPHA.length)]!), paths };
  }
  return null;
}

function makePuzzle(pool: Word[]): Puzzle | null {
  const words = snakeWords(pool); if (!words.length) return null;
  const tot = words.reduce((a, w) => a + w.w.length, 0); const N = tot <= 17 ? 5 : 6;
  const P = placeSnake(words, N); return P ? { words, N, ...P } : null;
}

export function Snake({ pool }: { pool: Word[] }) {
  const [round, setRound] = useState(0);
  return <SnakeGame key={round} pool={pool} again={() => setRound((r) => r + 1)} />;
}

function SnakeGame({ pool, again }: { pool: Word[]; again: () => void }) {
  const [P] = useState(() => makePuzzle(pool));
  const [found, setFound] = useState<boolean[]>(() => (P ? P.words.map(() => false) : []));
  const [locked, setLocked] = useState<Map<number, string>>(() => new Map());
  const [path, setPath] = useState<number[]>([]);
  const [pts, setPts] = useState('');
  const [hints, setHints] = useState(0);
  const [pulse, setPulse] = useState<number | null>(null);
  const [end, setEnd] = useState<{ sec: number; miss: number; xp: number } | null>(null);
  const grid = useRef<HTMLDivElement>(null);
  const [t0] = useState(() => Date.now());
  const R = useRef({ down: false, path: [] as number[], miss: 0, hints: 0, found: [] as boolean[], locked: new Map<number, string>(), alive: true });

  useEffect(() => {
    const r = R.current; r.alive = true; if (P) r.found = P.words.map(() => false);
    if (!P) { toast(t('Qayta urinib koʻring')); nav.pop(); }
    return () => { r.alive = false; };
  }, [P]);
  if (!P) return null;
  const { words, N, g } = P;

  const cellCenter = (i: number) => {
    const gr = grid.current!.getBoundingClientRect(), c = grid.current!.querySelector<HTMLElement>(`.sc[data-i="${i}"]`);
    if (!c) return '0,0'; const b = c.getBoundingClientRect();
    return `${b.left - gr.left + b.width / 2},${b.top - gr.top + b.height / 2}`;
  };
  const show = (p: number[]) => { R.current.path = p; setPath(p); setPts(p.length > 1 ? p.map(cellCenter).join(' ') : ''); };
  const adj = (a: number, b: number) => Math.abs(Math.floor(a / N) - Math.floor(b / N)) + Math.abs((a % N) - (b % N)) === 1;
  const at = (e: RPointerEvent) => {
    const el = document.elementFromPoint(e.clientX, e.clientY); const c = el?.closest<HTMLElement>('.sc');
    return c && grid.current?.contains(c) ? +c.dataset.i! : null;
  };

  const onDown = (e: RPointerEvent<HTMLDivElement>) => {
    const i = at(e); if (i === null || R.current.locked.has(i) || end) return;
    R.current.down = true; try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    show([i]); e.preventDefault();
  };
  const onMove = (e: RPointerEvent<HTMLDivElement>) => {
    const r = R.current; if (!r.down) return; const i = at(e); const p = r.path;
    if (i === null || i === p[p.length - 1]) return;
    if (p.length > 1 && i === p[p.length - 2]) { show(p.slice(0, -1)); return; } // orqaga qaytish — oxirgi harfni olib tashlaydi
    if (!p.includes(i) && !r.locked.has(i) && adj(p[p.length - 1]!, i)) show([...p, i]);
  };
  const onUp = () => {
    const r = R.current; if (!r.down) return; r.down = false;
    const p = r.path, word = p.map((i) => g[i]).join('');
    const k = words.findIndex((w, j) => !r.found[j] && fold(w.w) === fold(word));
    if (k >= 0) {
      const nf = r.found.map((f, j) => f || j === k), nl = new Map(r.locked); p.forEach((i) => nl.set(i, COLS[k]!));
      r.found = nf; r.locked = nl; setFound(nf); setLocked(nl); setPulse(null);
      chime(); haptic.ok(); speak(words[k]!.w); show([]);
      if (nf.every(Boolean)) finish();
    } else {
      if (p.length > 1) {
        r.miss++; haptic.bad();
        const gg = grid.current; if (gg) { gg.classList.remove('shake'); void gg.offsetWidth; gg.classList.add('shake'); }
      }
      show([]);
    }
  };
  const hint = () => {
    const i = found.findIndex((f) => !f); if (i < 0) return;
    R.current.hints = Math.max(R.current.hints, i + 1); setHints(R.current.hints); setPulse(P.paths[i]![0]!);
  };
  const finish = () => {
    const r = R.current, sec = Math.round((Date.now() - t0) / 1000), miss = r.miss, hs = r.hints;
    const xp = estXp(Math.min(XPR.gameCap, words.length * 5 + Math.max(0, 10 - miss * 2 - hs * 3)));
    S().set({ right: S().right + words.length });
    setEnd({ sec, miss, xp });
    fire(sync.game({ game: 'snake', score: words.length, mistakes: miss, hints: hs }).then((res) => {
      applyCourse(res.course);
      if (r.alive) setEnd((e) => (e ? { ...e, xp: res.xp } : e));
    }), 'game');
  };

  const done = found.filter(Boolean).length;
  return (
    <>
      <div className="sbar">
        <button className="x" aria-label={t('Chiqish')} onClick={() => nav.home('games')}><Ic n="x" /></button>
        <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 16 }}>{t('Soʻz ilon')}</b>
        <b>{done}/{words.length}</b>
      </div>
      <div className="sbody fade" style={{ gap: 16, paddingTop: 6 }}>
        <div className="slots">
          {words.map((w, i) => (
            <div key={i} className={`slot ${found[i] ? 'done' : ''}`} style={{ ['--c' as string]: COLS[i] }}>
              <span className="tr">{w.uz}</span>
              <div className="cells2">{[...UP(w.w)].map((ch, k) => <i key={k}>{found[i] || (k === 0 && hints > i) ? ch : ''}</i>)}</div>
            </div>
          ))}
        </div>
        <div className="sgrid-wrap">
          <div className="sgrid" ref={grid} style={{ gridTemplateColumns: `repeat(${N},1fr)`, pointerEvents: end ? 'none' : undefined }}
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
            {g.map((ch, i) => {
              const lc = locked.get(i);
              return (
                <span key={i} data-i={i} className={`sc${path.includes(i) ? ' sel' : ''}${pulse === i ? ' pulse' : ''}`}
                  style={lc ? { background: lc, color: '#fff', borderColor: 'transparent' } : undefined}>{ch}</span>
              );
            })}
          </div>
          <svg className="sline">
            {pts && <polyline points={pts} fill="none" stroke="rgba(26,102,245,.55)" strokeWidth={14} strokeLinecap="round" strokeLinejoin="round" />}
          </svg>
        </div>
        <div className="between">
          <span className="t12" style={{ fontWeight: 800, fontSize: 16, color: 'var(--primary-ink)', minHeight: 22 }}>{path.map((i) => g[i]).join('')}</span>
          <button className="btn soft" style={{ height: 40, width: 'auto', padding: '0 14px', fontSize: 14 }} onClick={hint} disabled={!!end}>
            <Ic n="bulb" size={16} />{t('Yordam')}
          </button>
        </div>
        <span className="t12" style={{ textAlign: 'center' }}>{t('Harflarni barmoq bilan ilondek bogʻlab soʻz yasang')}</span>
      </div>
      {end && (
        <div id="ft">
          <div className="sheet ok">
            <div className="hd">
              <i><Ic n="check" style={{ width: 22, height: 22, strokeWidth: 2.6 }} /></i>
              <b>{t('Hammasi topildi!')}</b>
              <span className="xp">+{end.xp} XP</span>
            </div>
            <p className="msg" style={{ color: 'var(--success-ink)' }}>{fmtTime(end.sec)} · {end.miss ? tn('# ta xato', end.miss) : t('Xatosiz')}</p>
            <div className="row g10">
              <button className="btn soft" style={{ background: '#fff', color: 'var(--success-ink)' }} onClick={again}>{t('Yana oʻynash')}</button>
              <button className="btn" onClick={() => nav.home('games')}>{t('Oʻyinlarga')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
